import { createApi } from '@reduxjs/toolkit/query/react';
import { z } from 'zod';
import { baseQuery } from './baseQuery';
import { validateResponse } from './validateResponse';

const nullableString = z.string().nullish().transform((value) => value ?? null);
const nullableDate = z.string().nullish().transform((value) => value ?? null);
const nullableNumber = z.number().nullish().transform((value) => value ?? null);

export const HomeStatusSchema = z.enum([
    'open',
    'inProgress',
    'completed',
    'cancelled',
    'unknown',
]);

export const HomeDashboardSchema = z.object({
    generatedAtUtc: z.string(),
    user: z.object({
        displayName: z.string(),
        role: nullableString,
        team: nullableString,
        territory: nullableString,
    }),
    attention: z.object({
        workflowActionCount: z.number().int().nonnegative(),
        approvalCount: z.number().int().nonnegative(),
        revisionCount: z.number().int().nonnegative(),
        closeoutCount: z.number().int().nonnegative(),
        overdueCount: z.number().int().nonnegative(),
    }),
    requestSummary: z.object({
        openCount: z.number().int().nonnegative(),
        inProgressCount: z.number().int().nonnegative(),
        completedLast30DaysCount: z.number().int().nonnegative(),
        statusBuckets: z.array(z.object({
            status: HomeStatusSchema,
            count: z.number().int().nonnegative(),
        })),
        dailyActivity: z.array(z.object({
            date: z.string(),
            submittedCount: z.number().int().nonnegative(),
            completedCount: z.number().int().nonnegative(),
        })),
    }),
    workflowItems: z.array(z.object({
        workflowInstanceId: z.number().int().positive(),
        actionType: z.enum(['approval', 'revision', 'closeout']),
        actionLabel: z.string(),
        status: z.string(),
        organizationName: z.string(),
        programTitle: z.string(),
        eventStartUtc: z.string(),
        eventEndUtc: nullableDate,
        submittedAtUtc: nullableDate,
        currentApproverName: nullableString,
        dueAtUtc: nullableDate,
        isOverdue: z.boolean(),
        destination: z.string(),
    })),
    exercises: z.array(z.object({
        id: z.string(),
        name: z.string(),
        startDateUtc: nullableDate,
        endDateUtc: nullableDate,
        normalizedStatus: z.string(),
        progressCurrent: z.number().int().nonnegative(),
        progressTotal: z.number().int().positive(),
        progressKind: z.string(),
        destination: z.string(),
        team: nullableString,
    })),
    recentRequests: z.array(z.object({
        itemKey: z.string(),
        actionId: nullableNumber,
        entityType: z.string(),
        entityId: nullableString,
        entityName: z.string(),
        requestType: z.string(),
        normalizedStatus: HomeStatusSchema,
        requestedAtUtc: nullableDate,
        updatedAtUtc: nullableDate,
        canEdit: z.boolean(),
        canCancel: z.boolean(),
    })),
});

export const HomeRequestDetailSchema = z.object({
    itemKey: z.string(),
    actionId: nullableNumber,
    entityType: z.string(),
    entityId: nullableString,
    entityName: z.string(),
    requestType: z.string(),
    normalizedStatus: HomeStatusSchema,
    requestedAtUtc: nullableDate,
    updatedAtUtc: nullableDate,
    requesterNote: nullableString,
    stewardNote: nullableString,
    stewardName: nullableString,
    previousValue: nullableString,
    newValue: nullableString,
    recordDestination: nullableString,
});

export type HomeStatus = z.infer<typeof HomeStatusSchema>;
export type HomeDashboard = z.infer<typeof HomeDashboardSchema>;
export type HomeRequestDetail = z.infer<typeof HomeRequestDetailSchema>;

export const homeV3Api = createApi({
    reducerPath: 'homeV3Api',
    baseQuery,
    tagTypes: ['HomeDashboard', 'HomeRequest'],
    refetchOnFocus: true,
    refetchOnReconnect: true,
    endpoints: (builder) => ({
        getHomeDashboard: builder.query<HomeDashboard, void>({
            query: () => 'api/v3/home/dashboard',
            transformResponse: validateResponse(HomeDashboardSchema),
            keepUnusedDataFor: 60,
            providesTags: ['HomeDashboard'],
        }),
        getHomeRequestDetail: builder.query<HomeRequestDetail, string>({
            query: (itemKey) => `api/v3/home/requests/${encodeURIComponent(itemKey)}`,
            transformResponse: validateResponse(HomeRequestDetailSchema),
            providesTags: (_result, _error, itemKey) => [{ type: 'HomeRequest', id: itemKey }],
        }),
    }),
});

export const {
    useGetHomeDashboardQuery,
    useLazyGetHomeRequestDetailQuery,
} = homeV3Api;
